import React, { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, ImageIcon, X, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const RECOMMENDED = "1200 × 675 px (16:9), JPG / PNG / WEBP, under 3 MB";

function resolveUrl(url) {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    // Relative /api/files/public/ID → prefix with API base
    return `${API}${url.replace(/^\/api/, "")}`;
}

export default function ThumbnailUploader({ value, onChange, testIdPrefix = "thumb" }) {
    const [uploading, setUploading] = useState(false);
    const [showUrl, setShowUrl] = useState(false);
    const inputRef = useRef(null);

    const handleFiles = async (files) => {
        const file = files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file");
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Image too large (max 3 MB). Please compress first.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/thumbnail", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Thumbnail uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const clear = () => onChange("");

    const previewSrc = resolveUrl(value);

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-uploader`}>
            <div className="flex items-center justify-between">
                <Label>Thumbnail (optional)</Label>
                <button
                    type="button"
                    onClick={() => setShowUrl((v) => !v)}
                    className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                    data-testid={`${testIdPrefix}-toggle-url`}
                >
                    <LinkIcon className="h-3 w-3" />
                    {showUrl ? "Upload file instead" : "Paste URL instead"}
                </button>
            </div>

            {previewSrc ? (
                <div
                    className="relative border border-border rounded-sm overflow-hidden bg-secondary/20"
                    data-testid={`${testIdPrefix}-preview`}
                >
                    {/* 16:9 aspect frame */}
                    <div className="relative pt-[56.25%]">
                        <img
                            src={previewSrc}
                            alt="Program thumbnail preview"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={clear}
                        className="absolute top-2 right-2 bg-background/90 hover:bg-background text-foreground rounded-full p-1.5 shadow-sm"
                        aria-label="Remove thumbnail"
                        data-testid={`${testIdPrefix}-clear`}
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : showUrl ? (
                <Input
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://…/image.jpg"
                    data-testid={`${testIdPrefix}-url-input`}
                />
            ) : (
                <label
                    htmlFor={`${testIdPrefix}-file-input`}
                    className="block border-2 border-dashed border-border rounded-sm p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        handleFiles(e.dataTransfer.files);
                    }}
                    data-testid={`${testIdPrefix}-dropzone`}
                >
                    <input
                        id={`${testIdPrefix}-file-input`}
                        ref={inputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => handleFiles(e.target.files)}
                        data-testid={`${testIdPrefix}-file-input`}
                    />
                    {uploading ? (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
                            <Upload className="h-4 w-4 animate-pulse" />
                            Uploading…
                        </div>
                    ) : (
                        <>
                            <ImageIcon className="h-7 w-7 text-muted-foreground mx-auto mb-1.5" />
                            <div className="text-sm font-medium text-foreground">
                                Click to upload or drag & drop
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                Recommended: {RECOMMENDED}
                            </div>
                        </>
                    )}
                </label>
            )}

            {previewSrc && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Recommended dimension: {RECOMMENDED}</span>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clear}
                        className="h-7"
                        data-testid={`${testIdPrefix}-replace`}
                    >
                        Replace
                    </Button>
                </div>
            )}
        </div>
    );
}
